import { getStorageCompanyId } from '@/utils/localStorage';

interface Access {
  isDemo: boolean;
  isSuperUser: boolean;
  isAdminOrSuperUser: boolean;
  canAccessFinance: boolean;
  canAccessAdsManagement: boolean;
  canReadMediaReports: boolean;
  canAccessCustomerService: boolean;
  canAccessCompanyInfo: boolean;
  canReadAia: boolean;
  canReadGenie: boolean;
  canReadTracking: boolean;
  canReadGA4bg: boolean;
  isGeniedemoUser: boolean;
}

type InitialStateType = {
  collapsed: boolean;
  currentUser?: API.User;
  accessibleModules?: API.CompanyModuleSubscription[];
};

const LIMITED_USER_EMAILS = [
  'angushsiao@mobagel.com',
  '5a@mobagel.com',
  'vivaia@mobagel.com',
  'fangzih@mobagel.com',
  'bibian@mobagel.com',
  'admin@mobagel.com',
];

// 模組代碼常數
const MODULE_CODE = {
  AI_ANALYTICS: 'ai_analytics', // AI智慧分析模組 (customer-journey, audience)
  ADS_MANAGEMENT: 'ads_management', // 廣告帳戶管理模組 (campaign)
  MEDIA_REPORTS: 'media_reports', // 媒體報表模組
} as const;

/**
 * 檢查是否有訂閱指定模組
 */
function hasModuleSubscription(
  accessibleModules: API.CompanyModuleSubscription[] | undefined,
  moduleCode: string,
): boolean {
  if (!accessibleModules || accessibleModules.length === 0) return false;
  return accessibleModules.some((sub) => sub.module?.code === moduleCode && sub.isActive);
}

/**
 * 檢查用戶是否為任一公司的 ADMIN
 */
function isAdminInAnyCompany(companyRoles: API.UserCompanyRole[] | undefined): boolean {
  if (!companyRoles || companyRoles.length === 0) return false;
  return companyRoles.some((role) => role.role === 'admin' && role.isActive);
}

/**
 * 檢查用戶在指定公司是否有 FINANCE 或 ADMIN 角色
 */
function hasFinanceAccess(
  companyRoles: API.UserCompanyRole[] | undefined,
  companyId: string | undefined,
): boolean {
  if (!companyRoles || companyRoles.length === 0 || !companyId) return false;
  return companyRoles.some(
    (role) =>
      role.companyId === companyId &&
      role.isActive &&
      (role.role === 'finance' || role.role === 'admin'),
  );
}

/**
 * 檢查用戶是否在任一公司有任何 active 角色
 * 用於：媒體報表（不需訂閱，公司任一成員均可檢視）
 */
function hasAnyActiveCompanyRole(companyRoles: API.UserCompanyRole[] | undefined): boolean {
  if (!companyRoles || companyRoles.length === 0) return false;
  return companyRoles.some((role) => role.isActive);
}

export default function access(initialState: InitialStateType): Access {
  const isDemo = window.location.hostname.startsWith('demo');
  const isSuperUser = initialState?.currentUser?.isSuperuser ?? false;
  const selectCompanyId = getStorageCompanyId();
  const selectCompany = initialState?.currentUser?.companies?.find(
    (company) => company.id === selectCompanyId,
  );

  // 從 initialState 取得可用模組訂閱
  const accessibleModules = initialState?.accessibleModules;

  // 使用新的模組訂閱系統判斷權限
  // AI 智慧分析模組 (customer-journey, audience)
  const hasAiAnalytics = hasModuleSubscription(accessibleModules, MODULE_CODE.AI_ANALYTICS);
  // 廣告帳戶管理模組 (campaign)
  const hasAdsManagement = hasModuleSubscription(accessibleModules, MODULE_CODE.ADS_MANAGEMENT);

  // 向下相容：如果沒有訂閱資料，fallback 到舊的 useAia/useGenie
  const { useAia = false, useGenie = false } = selectCompany || {};
  const canReadAia = hasAiAnalytics || useAia;
  const canReadGenie = hasAdsManagement || useGenie;

  const isLimitedUser = LIMITED_USER_EMAILS.includes(initialState?.currentUser?.email || '');
  const isGeniedemoUser = selectCompany?.clientId === 'demo';

  const companyRoles = initialState?.currentUser?.companyRoles;
  const isAdminOrSuperUser = isSuperUser || isAdminInAnyCompany(companyRoles);
  const canAccessFinance =
    isSuperUser || hasFinanceAccess(companyRoles, selectCompanyId ?? undefined);

  // 媒體報表（不需訂閱，任一公司任何 active 角色即可檢視）
  const canReadMediaReports = isSuperUser || hasAnyActiveCompanyRole(companyRoles);
  // 廣告帳戶管理（需 ads_management 訂閱；向下相容 useGenie）
  const canAccessAdsManagement = isSuperUser || canReadGenie;
  // 客戶服務中心（登入即可進入此 menu，子項目各自判斷）
  const canAccessCustomerService = !!initialState?.currentUser;
  // 公司資料（含綁定公司功能）：首次綁定（無任何公司角色）或現有公司 ADMIN/superuser
  // 對應後端 allow_first_binding_or_admin
  const hasNoCompanyRole = !companyRoles || companyRoles.length === 0;
  const canAccessCompanyInfo = isAdminOrSuperUser || hasNoCompanyRole;

  return {
    isDemo,
    isSuperUser,
    isAdminOrSuperUser,
    canAccessFinance,
    canAccessAdsManagement,
    canReadMediaReports,
    canAccessCustomerService,
    canAccessCompanyInfo,
    canReadAia,
    canReadGenie,
    canReadTracking: canReadAia || canReadGenie,
    canReadGA4bg: isLimitedUser || canReadAia, // AI 智慧分析模組控制 customer-journey
    isGeniedemoUser,
  };
}
